import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { Button, Input } from "@/design-system";

interface Opcao {
  companyId: string;
  companyName: string | null;
}

interface CompanyComboboxProps {
  opcoes: Opcao[];
  valor?: string;
  onChange: (companyId: string | undefined) => void;
  disabled?: boolean;
}

/**
 * Seletor de empresa com busca.
 *
 * **Por que não um `<select>` nativo.** Em produção são mais de cem clientes, e
 * um select nativo obriga a rolar a lista inteira procurando pelo nome. Com
 * busca, digitar três letras basta.
 *
 * O botão de limpar é separado da lista de propósito: "todas as empresas" é o
 * estado inicial da tela, não uma opção entre as outras — misturar as duas
 * coisas faz o usuário procurar na lista o que é, na verdade, um reset.
 */
export function CompanyCombobox({
  opcoes,
  valor,
  onChange,
  disabled,
}: CompanyComboboxProps) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selecionada = opcoes.find((o) => o.companyId === valor);

  const filtradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const lista = [...opcoes].sort((a, b) =>
      (a.companyName ?? "").localeCompare(b.companyName ?? ""),
    );
    if (!termo) return lista;
    return lista.filter((o) =>
      (o.companyName ?? "").toLowerCase().includes(termo),
    );
  }, [opcoes, busca]);

  // Fecha ao clicar fora. Sem isso o painel fica aberto sobre a tabela e o
  // usuário perde a referência do que estava lendo.
  useEffect(() => {
    if (!aberto) return;
    const aoClicarFora = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setAberto(false);
        setBusca("");
      }
    };
    document.addEventListener("mousedown", aoClicarFora);
    return () => document.removeEventListener("mousedown", aoClicarFora);
  }, [aberto]);

  useEffect(() => {
    if (aberto) inputRef.current?.focus();
  }, [aberto]);

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center gap-1">
        <Button variant="outline" size="sm"
          type="button"
          onClick={() => setAberto((v) => !v)}
          disabled={disabled}
          aria-expanded={aberto}
          aria-haspopup="listbox"
          aria-controls="company-combobox-options"
          className="min-w-[240px] justify-between text-left text-sm text-gray-900 shadow-sm hover:border-gray-400 focus:border-violet-500"
        >
          <span className="truncate">
            {selecionada?.companyName ?? "Todas as empresas"}
          </span>
          <ChevronDown size={15} className="shrink-0 text-gray-400" />
        </Button>

        {valor && (
          <Button variant="outline" size="sm"
            type="button"
            onClick={() => {
              onChange(undefined);
              setBusca("");
            }}
            disabled={disabled}
            title="Ver todas as empresas"
            aria-label="Ver todas as empresas"
            className="p-2 text-gray-500 shadow-sm hover:bg-gray-50 hover:text-gray-700"
          >
            <X size={15} />
          </Button>
        )}
      </div>

      {aberto && (
        <div id="company-combobox-options" role="listbox" className="absolute z-20 mt-1 w-[320px] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
          <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2">
            <Search size={14} className="shrink-0 text-gray-400" />
            <div className="min-w-0 flex-1">
            <Input
              type="text"
              ref={inputRef}
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar empresa..."
              aria-label="Buscar empresa"
              className="w-full px-0 text-sm text-gray-900 placeholder:text-gray-400 shadow-none focus:ring-0"
            />
            </div>
          </div>

          <div className="max-h-72 overflow-y-auto py-1">
            <Button variant="ghost" size="sm"
              type="button"
              onClick={() => {
                onChange(undefined);
                setAberto(false);
                setBusca("");
              }}
              className="h-auto w-full justify-between px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
            >
              <span className="font-medium">Todas as empresas</span>
              {!valor && <Check size={15} className="text-violet-600" />}
            </Button>

            <div className="my-1 border-t border-gray-100" />

            {filtradas.length === 0 ? (
              <p className="px-3 py-4 text-center text-sm text-gray-500">
                Nenhuma empresa encontrada.
              </p>
            ) : (
              filtradas.map((o) => (
                <Button variant="ghost" size="sm"
                  key={o.companyId}
                  type="button"
                  onClick={() => {
                    onChange(o.companyId);
                    setAberto(false);
                    setBusca("");
                  }}
                  className="h-auto w-full justify-between gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                >
                  <span className="truncate">{o.companyName}</span>
                  {o.companyId === valor && (
                    <Check size={15} className="shrink-0 text-violet-600" />
                  )}
                </Button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
